"use client"

/**
 * Ask Leo waiting row — DS Message marker format:
 * Leo star (working) stays visible; only the content slot skeletons.
 *
 * Matches `Marker` + `LeoIcon` + `MarkerContent` from the Message catalog.
 */

import { LeoIcon } from "@/components/ui/leo-icon"
import { Marker, MarkerContent, MarkerIcon } from "@/components/ui/marker"
import { Message, MessageContent } from "@/components/ui/message"
import { Skeleton } from "@/components/ui/skeleton"
import { LEO_OPTION_SET_CARD_CLASS } from "@/components/question-quality/question-quality-finding"
import { cn } from "@/lib/utils"

const WAVE_BAR_COUNT = 5

export function LeoThinkingWaveLoader({
  className,
  size = "md",
}: {
  className?: string
  size?: "sm" | "md"
}) {
  const barHeight = size === "sm" ? "h-3.5" : "h-5"
  const barCount = size === "sm" ? 3 : WAVE_BAR_COUNT
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-[3px]",
        barHeight,
        className,
      )}
      aria-hidden="true"
    >
      {Array.from({ length: barCount }, (_, index) => (
        <span
          key={index}
          className={cn(
            "leo-thinking-wave-bar w-[3px] shrink-0 rounded-full bg-brand/50",
            barHeight,
          )}
          style={{ animationDelay: `${index * 0.11}s` }}
        />
      ))}
    </div>
  )
}

const TEXT_SKELETON_WIDTHS = ["w-4/5", "w-3/5", "w-2/3"] as const

/** Text/content skeleton — logo stays outside this slot. */
export function LeoThinkingContentSkeleton({
  className,
  rows = 2,
}: {
  className?: string
  rows?: number
}) {
  return (
    <div
      className={cn("flex min-w-0 flex-1 flex-col gap-2 py-0.5", className)}
      aria-hidden="true"
    >
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton
          key={index}
          className={cn("h-3", TEXT_SKELETON_WIDTHS[index] ?? "w-1/2")}
        />
      ))}
    </div>
  )
}

/** @deprecated Use LeoThinkingContentSkeleton beside the Leo star. */
export function LeoThinkingTextLoader({ className }: { className?: string }) {
  return <LeoThinkingContentSkeleton className={className} />
}

/** Pager card body placeholder — shell and stepper stay visible. */
export function LeoCatalogCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("space-y-3", LEO_OPTION_SET_CARD_CLASS, className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Loading suggestion</span>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <Skeleton className="h-6 w-16 shrink-0 rounded-full" />
      </div>
      <Skeleton className="h-8 w-28 rounded-md" />
      <Skeleton className="h-3 w-full" />
    </div>
  )
}

/** @deprecated Use LeoCatalogCardSkeleton inside the pager shell. */
export function LeoCatalogPageLoader({ className }: { className?: string }) {
  return <LeoCatalogCardSkeleton className={className} />
}

export type LeoThinkingWidgetContent = "label" | "loader"

export function LeoThinkingWidget({
  className,
  content = "label",
  size = "md",
}: {
  className?: string
  /** Ask Leo panel uses label; canvas fields use the wave loader. */
  content?: LeoThinkingWidgetContent
  size?: "sm" | "md"
}) {
  const iconSize = size === "sm" ? "size-4" : "size-5"
  return (
    <Marker
      role="status"
      aria-live="polite"
      className={cn(content === "loader" && "gap-1.5", className)}
    >
      <MarkerIcon className={iconSize}>
        <LeoIcon variant="ambient" size="xs" state="working" />
      </MarkerIcon>
      <MarkerContent className="min-w-0 flex-1 font-medium">
        {content === "label" ? (
          <span className="text-sm text-muted-foreground">Thinking</span>
        ) : (
          <>
            <span className="sr-only">Leo is thinking</span>
            <LeoThinkingWaveLoader size={size === "sm" ? "sm" : "md"} />
          </>
        )}
      </MarkerContent>
    </Marker>
  )
}

/** Same widget, aligned like other assistant turns in the thread. */
export function LeoThinkingThreadRow({ className }: { className?: string }) {
  return (
    <Message className={className}>
      <MessageContent className="min-w-0">
        <LeoThinkingWidget />
      </MessageContent>
    </Message>
  )
}
